import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

type ApproveStoppersResponse = {
  code: string;
  message: string;
  processId: UUID;
};

/**
 * Approves the stoppers for the given invoice process.
 * @param processId - The ID of the invoice process.
 * @returns A promise that resolves when the stoppers are approved.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/approve_stoppers_invoice_process__process_id__approve_stoppers_put
 */
export const approveStoppers = async (processId: UUID) => {
  try {
    const response = await vestigasApi
      .put(ENDPOINT.INVOICE_REPROCESSING.APPROVE_STOPPERS(processId))
      .json<ApproveStoppersResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error approving invoice stoppers',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(($) => $.approveStoppers.error404, {
            ns: 'data',
          });
          break;
        }

        case 422: {
          errorMessage = i18n.t(($) => $.approveStoppers.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.approveStoppers.error.generic, {
            ns: 'data',
          });
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
